import React, { useContext } from 'react';
import type { GridApi } from 'ag-grid-community';

import useIsDataTable from 'components/DataTable/useIsDataTable';
import NoDataToDisplay from 'components/V5Gadget/NoDataToDisplay';
import BaseViewsContext from 'contexts/BaseViewsContext';
import GridContext from 'contexts/GridContext';

import GridContainerGate from './index';
import RecordsTable from './RecordsTable';

// A records grid that moves to DataTable surface by surface: RecordsTable
// where useIsDataTable(surface) says so, else AG's GridContainer with the
// same props. Opt-in per caller.
const RecordsGrid = ({
  surface,
  sizing,
  skipHeaderWhenSizing,
  isResizable,
  onGridReady,
  onExportHandle,
  slideOut,
  ...gridProps
}: React.ComponentProps<typeof GridContainerGate> & {
  surface: DataTable.Surface;
  // How DataTable sizes the columns, to match what the AG props do
  sizing: DataTable.SizingMode;
  skipHeaderWhenSizing?: boolean;
  isResizable?: boolean;
  // DataTable only: AG's grid hands its api to the GridProvider itself
  onGridReady?: (event: { api: GridApi }) => void;
  // DataTable only: AG's grid has no export handle
  onExportHandle?: (handle: DataTable.ExportHandle | undefined) => void;
  // DataTable only: a board's slide-out, which AG's Grid draws itself
  slideOut?: React.ReactNode;
}) => {
  const isDataTable = useIsDataTable(surface);
  const { baseViews } = useContext(BaseViewsContext);
  const { dataType, baseViewOverride, sortOverride, gridId } =
    useContext(GridContext);

  if (!isDataTable) {
    return <GridContainerGate {...gridProps} />;
  }

  const baseView = baseViewOverride ? baseViewOverride : baseViews[dataType];
  if (baseView === undefined) {
    return <NoDataToDisplay size={'normal'} />;
  }

  return (
    <RecordsTable
      // Restarting the grid (an expired search session) starts it afresh
      key={gridId}
      baseView={baseView}
      sort={sortOverride}
      sizing={sizing}
      skipHeaderWhenSizing={skipHeaderWhenSizing}
      // As GridContainer builds its columns: they share the width when the
      // layout is off or one column is fitted
      isFlexLayout={
        gridProps.disableAutoLayout ||
        gridProps.autoSizeFirstColumnOnly ||
        gridProps.autoSizeSecondColumnOnly
      }
      fittedColumn={
        gridProps.autoSizeFirstColumnOnly
          ? 'first'
          : gridProps.autoSizeSecondColumnOnly
            ? 'second'
            : undefined
      }
      isResizable={isResizable}
      hasBorders={gridProps.hasBorders}
      hasDarkHeader={gridProps.hasDarkHeader}
      isSearchScreen={gridProps.isSearchScreen}
      hideTotalsRow={gridProps.hideTotalsRow}
      getContextMenuItems={gridProps.getContextMenuItems}
      onGridReady={onGridReady}
      onExportHandle={onExportHandle}
      slideOut={slideOut}
    />
  );
};

export default RecordsGrid;
